import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  InboxIcon
} from '@heroicons/react/24/outline'

// Interfaces
interface Envio {
  id: string
  numeroGuia: string
  remitente: {
    nombre: string
    telefono: string
    direccion: string
    empresa?: string
  }
  destinatario: {
    nombre: string
    telefono: string
    direccion: string
    empresa?: string
  }
  paquete: {
    descripcion: string
    peso: number
    dimensiones: { largo: number; ancho: number; alto: number }
    valor: number
    categoria?: string
  }
  estado: 'pendiente' | 'recolectado' | 'en_transito' | 'entregado' | 'cancelado' | 'devuelto'
  fechaCreacion: string
  fechaEntregaEstimada: string
  fechaEntregaReal?: string
  costo: number
  metodoPago: string
  prioridad: 'alta' | 'media' | 'baja'
  conductor?: {
    id: string
    nombre: string
    telefono: string
  }
  vehiculo?: {
    id: string
    placa: string
    tipo: string
  }
  observaciones?: string
}

interface FiltrosEnvio {
  busqueda: string
  estado: string
  fechaInicio: string
  fechaFin: string
  prioridad: string
  metodoPago: string
}

const PaginaPanelEnvios: React.FC = () => {
  const navigate = useNavigate()
  const [envios, setEnvios] = useState<Envio[]>([])
  const [enviosFiltrados, setEnviosFiltrados] = useState<Envio[]>([])
  const [cargando, setCargando] = useState(true)
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [envioSeleccionado, setEnvioSeleccionado] = useState<Envio | null>(null)
  const [mostrarModal, setMostrarModal] = useState(false)
  const [actualizando, setActualizando] = useState(false)
  const [seguimientoEnTiempoReal, setSeguimientoEnTiempoReal] = useState(false)
  const [ultimaActualizacion, setUltimaActualizacion] = useState<Date>(new Date())
  const [notificaciones, setNotificaciones] = useState<string[]>([])

  // Estados de filtros
  const [filtros, setFiltros] = useState<FiltrosEnvio>({
    busqueda: '',
    estado: '',
    fechaInicio: '',
    fechaFin: '',
    prioridad: '',
    metodoPago: ''
  })

  // Datos de ejemplo
  const enviosEjemplo: Envio[] = [
    {
      id: '1',
      numeroGuia: 'LXP-2024-001',
      remitente: {
        nombre: 'Juan Pérez',
        telefono: '+57 300 123 4567',
        direccion: 'Calle 123 #45-67, Bogotá',
        empresa: 'Empresa ABC'
      },
      destinatario: {
        nombre: 'María García',
        telefono: '+57 301 234 5678',
        direccion: 'Carrera 89 #12-34, Medellín'
      },
      paquete: {
        descripcion: 'Documentos importantes',
        peso: 0.5,
        dimensiones: { largo: 30, ancho: 20, alto: 5 },
        valor: 50000,
        categoria: 'Documentos'
      },
      estado: 'en_transito',
      fechaCreacion: '2024-01-15T10:30:00Z',
      fechaEntregaEstimada: '2024-01-17T16:00:00Z',
      costo: 25000,
      metodoPago: 'Efectivo',
      prioridad: 'alta',
      conductor: {
        id: 'c1',
        nombre: 'Carlos Rodríguez',
        telefono: '+57 302 345 6789'
      },
      vehiculo: {
        id: 'v1',
        placa: 'ABC-123',
        tipo: 'Motocicleta'
      }
    },
    {
      id: '2',
      numeroGuia: 'LXP-2024-002',
      remitente: {
        nombre: 'Ana López',
        telefono: '+57 303 456 7890',
        direccion: 'Avenida 68 #45-23, Bogotá'
      },
      destinatario: {
        nombre: 'Pedro Martínez',
        empresa: 'Tech Solutions',
        telefono: '+57 304 567 8901',
        direccion: 'Calle 50 #23-45, Cali'
      },
      paquete: {
        descripcion: 'Equipo electrónico',
        peso: 2.5,
        dimensiones: { largo: 40, ancho: 30, alto: 15 },
        valor: 500000,
        categoria: 'Electrónicos'
      },
      estado: 'pendiente',
      fechaCreacion: '2024-01-16T08:15:00Z',
      fechaEntregaEstimada: '2024-01-18T14:00:00Z',
      costo: 45000,
      metodoPago: 'Tarjeta',
      prioridad: 'media'
    },
    {
      id: '3',
      numeroGuia: 'LXP-2024-003',
      remitente: {
        nombre: 'Luis Martín',
        telefono: '+57 305 678 9012',
        direccion: 'Carrera 15 #78-90, Barranquilla',
        empresa: 'Comercial Norte'
      },
      destinatario: {
        nombre: 'Sofia Herrera',
        telefono: '+57 306 789 0123',
        direccion: 'Calle 85 #34-56, Cartagena'
      },
      paquete: {
        descripcion: 'Productos farmacéuticos',
        peso: 1.2,
        dimensiones: { largo: 25, ancho: 15, alto: 10 },
        valor: 120000,
        categoria: 'Farmacéuticos'
      },
      estado: 'entregado',
      fechaCreacion: '2024-01-14T14:20:00Z',
      fechaEntregaEstimada: '2024-01-16T10:00:00Z',
      fechaEntregaReal: '2024-01-16T09:45:00Z',
      costo: 35000,
      metodoPago: 'Transferencia',
      prioridad: 'alta',
      conductor: {
        id: 'c2',
        nombre: 'Diana Ruiz',
        telefono: '+57 307 890 1234'
      },
      vehiculo: {
        id: 'v2',
        placa: 'DEF-456',
        tipo: 'Camioneta'
      }
    },
    {
      id: '4',
      numeroGuia: 'LXP-2024-004',
      remitente: {
        nombre: 'Roberto Silva',
        telefono: '+57 308 901 2345',
        direccion: 'Avenida 19 #123-45, Bucaramanga'
      },
      destinatario: {
        nombre: 'Carmen Vega',
        telefono: '+57 309 012 3456',
        direccion: 'Calle 72 #89-12, Pereira'
      },
      paquete: {
        descripcion: 'Ropa y accesorios',
        peso: 3.0,
        dimensiones: { largo: 50, ancho: 40, alto: 20 },
        valor: 80000,
        categoria: 'Textil'
      },
      estado: 'recolectado',
      fechaCreacion: '2024-01-17T11:00:00Z',
      fechaEntregaEstimada: '2024-01-19T15:30:00Z',
      costo: 38000,
      metodoPago: 'Efectivo',
      prioridad: 'baja',
      observaciones: 'Manejar con cuidado, contiene artículos delicados'
    }
  ]

  // Cargar datos
  useEffect(() => {
    const cargarEnvios = async () => {
      setCargando(true)
      // Simular carga de datos
      await new Promise(resolve => setTimeout(resolve, 1000))
      setEnvios(enviosEjemplo)
      setEnviosFiltrados(enviosEjemplo)
      setCargando(false)
      setUltimaActualizacion(new Date())
    }

    cargarEnvios()
  }, [])

  // Seguimiento en tiempo real
  useEffect(() => {
    if (!seguimientoEnTiempoReal) return

    const intervalo = setInterval(async () => {
      // Simular actualización de datos en tiempo real
      const nuevosEnvios = [...envios]
      let hayActualizaciones = false

      // Simular cambios de estado aleatorios
      nuevosEnvios.forEach(envio => {
        if (Math.random() < 0.1) { // 10% de probabilidad de cambio
          const estadosDisponibles = ['pendiente', 'recolectado', 'en_transito', 'entregado']
          const estadoActualIndex = estadosDisponibles.indexOf(envio.estado)
          if (estadoActualIndex < estadosDisponibles.length - 1) {
            const nuevoEstado = estadosDisponibles[estadoActualIndex + 1]
            envio.estado = nuevoEstado as any
            hayActualizaciones = true
            
            // Agregar notificación
            const nuevaNotificacion = `Envío ${envio.numeroGuia} cambió a estado: ${nuevoEstado.replace('_', ' ')}`
            setNotificaciones(prev => [nuevaNotificacion, ...prev.slice(0, 4)]) // Mantener solo 5 notificaciones
          }
        }
      })

      if (hayActualizaciones) {
        setEnvios(nuevosEnvios)
        setUltimaActualizacion(new Date())
      }
    }, 30000) // Actualizar cada 30 segundos

    return () => clearInterval(intervalo)
  }, [seguimientoEnTiempoReal, envios])

  // Filtrar envíos
  useEffect(() => {
    let resultado = [...envios]

    // Filtro por búsqueda
    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase()
      resultado = resultado.filter(envio =>
        envio.numeroGuia.toLowerCase().includes(busqueda) ||
        envio.remitente.nombre.toLowerCase().includes(busqueda) ||
        envio.destinatario.nombre.toLowerCase().includes(busqueda) ||
        envio.paquete.descripcion.toLowerCase().includes(busqueda) ||
        (envio.conductor?.nombre.toLowerCase().includes(busqueda))
      )
    }

    // Filtro por estado
    if (filtros.estado) {
      resultado = resultado.filter(envio => envio.estado === filtros.estado)
    }

    // Filtro por prioridad
    if (filtros.prioridad) {
      resultado = resultado.filter(envio => envio.prioridad === filtros.prioridad)
    }

    // Filtro por método de pago
    if (filtros.metodoPago) {
      resultado = resultado.filter(envio => envio.metodoPago === filtros.metodoPago)
    }

    // Filtro por fechas
    if (filtros.fechaInicio) {
      resultado = resultado.filter(envio => 
        new Date(envio.fechaCreacion) >= new Date(filtros.fechaInicio)
      )
    }

    if (filtros.fechaFin) {
      resultado = resultado.filter(envio => 
        new Date(envio.fechaCreacion) <= new Date(filtros.fechaFin)
      )
    }

    setEnviosFiltrados(resultado)
  }, [filtros, envios])

  // Funciones auxiliares
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'recolectado':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'en_transito':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'entregado':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'devuelto':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return <ClockIcon className="h-4 w-4" />
      case 'recolectado':
        return <TruckIcon className="h-4 w-4" />
      case 'en_transito':
        return <ArrowPathIcon className="h-4 w-4" />
      case 'entregado':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'cancelado':
        return <XCircleIcon className="h-4 w-4" />
      case 'devuelto':
        return <ExclamationTriangleIcon className="h-4 w-4" />
      default:
        return <ClockIcon className="h-4 w-4" />
    }
  }

  const getPrioridadColor = (prioridad: string) => {
    switch (prioridad) {
      case 'alta':
        return 'border-red-200 text-red-700 bg-red-50'
      case 'media':
        return 'border-yellow-200 text-yellow-700 bg-yellow-50'
      case 'baja':
        return 'border-green-200 text-green-700 bg-green-50'
      default:
        return 'border-gray-200 text-gray-700 bg-gray-50'
    }
  }

  const formatearMoneda = (valor: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor)
  }

  const abrirModal = (envio: Envio) => {
    setEnvioSeleccionado(envio)
    setMostrarModal(true)
  }

  const cerrarModal = () => {
    setMostrarModal(false)
    setEnvioSeleccionado(null)
  }

  const actualizarEnvios = async () => {
    setActualizando(true)
    await new Promise(resolve => setTimeout(resolve, 1500))
    setUltimaActualizacion(new Date())
    setActualizando(false)
  }

  const limpiarNotificaciones = () => {
    setNotificaciones([])
  }

  const toggleSeguimientoTiempoReal = () => {
    setSeguimientoEnTiempoReal(!seguimientoEnTiempoReal)
    if (!seguimientoEnTiempoReal) {
      setNotificaciones(prev => ['Seguimiento en tiempo real activado', ...prev.slice(0, 4)])
    } else {
      setNotificaciones(prev => ['Seguimiento en tiempo real desactivado', ...prev.slice(0, 4)])
    }
  }

  // Calcular métricas
  const totalEnvios = enviosFiltrados.length
  const enviosPendientes = enviosFiltrados.filter(e => e.estado === 'pendiente').length
  const enviosEnTransito = enviosFiltrados.filter(e => e.estado === 'en_transito' || e.estado === 'recolectado').length
  const enviosEntregados = enviosFiltrados.filter(e => e.estado === 'entregado').length
  const ingresosTotales = enviosFiltrados.reduce((total, envio) => total + envio.costo, 0)

  if (cargando) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Panel de Envíos</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestiona todos tus envíos desde un solo lugar
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Indicador de seguimiento en tiempo real */}
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 rounded-full ${seguimientoEnTiempoReal ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {seguimientoEnTiempoReal ? 'En vivo' : 'Pausado'}
            </span>
          </div>

          {/* Última actualización */}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Última actualización: {ultimaActualizacion.toLocaleTimeString('es-ES')}
          </div>

          {/* Botón de seguimiento en tiempo real */}
          <button
            onClick={toggleSeguimientoTiempoReal}
            className={`inline-flex items-center px-3 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              seguimientoEnTiempoReal
                ? 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100 dark:border-green-600 dark:text-green-400 dark:bg-green-900 dark:hover:bg-green-800'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
            }`}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${seguimientoEnTiempoReal ? 'animate-spin' : ''}`} />
            {seguimientoEnTiempoReal ? 'Pausar' : 'Activar'} Seguimiento
          </button>

          <button
            onClick={actualizarEnvios}
            disabled={actualizando}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${actualizando ? 'animate-spin' : ''}`} />
            {actualizando ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button
            onClick={() => navigate('/dashboard/envios/crear')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Crear Envío
          </button>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <InboxIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Envíos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {totalEnvios}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Pendientes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {enviosPendientes}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TruckIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    En Tránsito
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {enviosEnTransito}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Entregados
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {enviosEntregados}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Ingresos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {formatearMoneda(ingresosTotales)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Panel de notificaciones */}
      {notificaciones.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse"></div>
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Notificaciones en Tiempo Real
                </h3>
              </div>
              <button
                onClick={limpiarNotificaciones}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
              >
                Limpiar
              </button>
            </div>
            <div className="space-y-2">
              {notificaciones.slice(0, 3).map((notificacion, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 text-sm text-blue-800 dark:text-blue-200"
                >
                  <div className="h-1 w-1 bg-blue-400 rounded-full"></div>
                  <span>{notificacion}</span>
                  <span className="text-xs text-blue-600 dark:text-blue-400">
                    {new Date().toLocaleTimeString('es-ES')}
                  </span>
                </div>
              ))}
              {notificaciones.length > 3 && (
                <div className="text-xs text-blue-600 dark:text-blue-400">
                  +{notificaciones.length - 3} notificaciones más
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Filtros y Búsqueda
            </h3>
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              {mostrarFiltros ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          <div className="space-y-4">
            {/* Búsqueda principal */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Buscar por número de guía, remitente, destinatario, descripción..."
              />
            </div>

            {/* Filtros adicionales */}
            {mostrarFiltros && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado
                  </label>
                  <select
                    value={filtros.estado}
                    onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="recolectado">Recolectado</option>
                    <option value="en_transito">En Tránsito</option>
                    <option value="entregado">Entregado</option>
                    <option value="cancelado">Cancelado</option>
                    <option value="devuelto">Devuelto</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Prioridad
                  </label>
                  <select
                    value={filtros.prioridad}
                    onChange={(e) => setFiltros({ ...filtros, prioridad: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Todas las prioridades</option>
                    <option value="alta">Alta</option>
                    <option value="media">Media</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Método de Pago
                  </label>
                  <select
                    value={filtros.metodoPago}
                    onChange={(e) => setFiltros({ ...filtros, metodoPago: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Todos los métodos</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Transferencia">Transferencia</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={filtros.fechaInicio}
                    onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={filtros.fechaFin}
                    onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lista de envíos */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Envíos ({enviosFiltrados.length})
            </h3>
          </div>

          <div className="space-y-4">
            {enviosFiltrados.map((envio) => (
              <div
                key={envio.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <TruckIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {envio.numeroGuia}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPrioridadColor(envio.prioridad)}`}>
                          {envio.prioridad.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4" />
                          <span>De: {envio.remitente.nombre}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <MapPinIcon className="h-4 w-4" />
                          <span>Para: {envio.destinatario.nombre}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <DocumentTextIcon className="h-4 w-4" />
                          <span>{envio.paquete.descripcion}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="h-4 w-4" />
                          <span>
                            {new Date(envio.fechaCreacion).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        {envio.conductor && (
                          <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4" />
                            <span>Conductor: {envio.conductor.nombre}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-green-600 dark:text-green-400">
                            {formatearMoneda(envio.costo)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 ml-4">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getEstadoColor(envio.estado)}`}>
                      {getEstadoIcon(envio.estado)}
                      <span className="ml-2 capitalize">{envio.estado.replace('_', ' ')}</span>
                    </div>
                    
                    <button
                      onClick={() => abrirModal(envio)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      Ver detalles
                    </button>
                    
                    <button
                      onClick={() => navigate(`/dashboard/envios/${envio.id}/seguimiento`)}
                      className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      Seguimiento
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {enviosFiltrados.length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto h-24 w-24 text-gray-400">
                <InboxIcon className="h-full w-full" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay envíos</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No se encontraron envíos con los filtros seleccionados.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/dashboard/envios/crear')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Crear primer envío
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalles */}
      {mostrarModal && envioSeleccionado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <TruckIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {envioSeleccionado.numeroGuia}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {envioSeleccionado.paquete.descripcion}
                  </p>
                </div>
              </div>
              <button
                onClick={cerrarModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Información del remitente */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Remitente</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Nombre:</span> {envioSeleccionado.remitente.nombre}
                    </p>
                    {envioSeleccionado.remitente.empresa && (
                      <p className="text-sm">
                        <span className="font-medium">Empresa:</span> {envioSeleccionado.remitente.empresa}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-medium">Teléfono:</span> {envioSeleccionado.remitente.telefono}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Dirección:</span> {envioSeleccionado.remitente.direccion}
                    </p>
                  </div>
                </div>
              </div>

              {/* Información del destinatario */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Destinatario</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Nombre:</span> {envioSeleccionado.destinatario.nombre}
                    </p>
                    {envioSeleccionado.destinatario.empresa && (
                      <p className="text-sm">
                        <span className="font-medium">Empresa:</span> {envioSeleccionado.destinatario.empresa}
                      </p>
                    )}
                    <p className="text-sm">
                      <span className="font-medium">Teléfono:</span> {envioSeleccionado.destinatario.telefono}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Dirección:</span> {envioSeleccionado.destinatario.direccion}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Información del paquete y envío */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Información del Paquete</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Descripción:</span> {envioSeleccionado.paquete.descripcion}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Peso:</span> {envioSeleccionado.paquete.peso} kg
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Dimensiones:</span> {envioSeleccionado.paquete.dimensiones.largo} x {envioSeleccionado.paquete.dimensiones.ancho} x {envioSeleccionado.paquete.dimensiones.alto} cm
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Valor:</span> {formatearMoneda(envioSeleccionado.paquete.valor)}
                    </p>
                    {envioSeleccionado.paquete.categoria && (
                      <p className="text-sm">
                        <span className="font-medium">Categoría:</span> {envioSeleccionado.paquete.categoria}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white">Detalles del Envío</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">Estado:</span>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(envioSeleccionado.estado)}`}>
                        {getEstadoIcon(envioSeleccionado.estado)}
                        <span className="ml-1 capitalize">{envioSeleccionado.estado.replace('_', ' ')}</span>
                      </div>
                    </div>
                    <p className="text-sm">
                      <span className="font-medium">Costo:</span> {formatearMoneda(envioSeleccionado.costo)}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Método de pago:</span> {envioSeleccionado.metodoPago}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Prioridad:</span> 
                      <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPrioridadColor(envioSeleccionado.prioridad)}`}>
                        {envioSeleccionado.prioridad.toUpperCase()}
                      </span>
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Fecha de creación:</span> {new Date(envioSeleccionado.fechaCreacion).toLocaleDateString('es-ES')}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Entrega estimada:</span> {new Date(envioSeleccionado.fechaEntregaEstimada).toLocaleDateString('es-ES')}
                    </p>
                    {envioSeleccionado.fechaEntregaReal && (
                      <p className="text-sm">
                        <span className="font-medium">Entrega real:</span> {new Date(envioSeleccionado.fechaEntregaReal).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Información del conductor y vehículo */}
            {(envioSeleccionado.conductor || envioSeleccionado.vehiculo) && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Transporte</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {envioSeleccionado.conductor && (
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">Conductor</h5>
                        <div className="space-y-1">
                          <p className="text-sm">
                            <span className="font-medium">Nombre:</span> {envioSeleccionado.conductor.nombre}
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Teléfono:</span> {envioSeleccionado.conductor.telefono}
                          </p>
                        </div>
                      </div>
                    )}
                    {envioSeleccionado.vehiculo && (
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-white mb-2">Vehículo</h5>
                        <div className="space-y-1">
                          <p className="text-sm">
                            <span className="font-medium">Placa:</span> {envioSeleccionado.vehiculo.placa}
                          </p>
                          <p className="text-sm">
                            <span className="font-medium">Tipo:</span> {envioSeleccionado.vehiculo.tipo}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Observaciones */}
            {envioSeleccionado.observaciones && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Observaciones</h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {envioSeleccionado.observaciones}
                  </p>
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => navigate(`/dashboard/envios/${envioSeleccionado.id}/seguimiento`)}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Ver Seguimiento
              </button>
              <button
                onClick={cerrarModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaPanelEnvios
